/**
 * Structured emitter for OneTrust consent telemetry.
 *
 * Ships each event to `/api/onetrust-event` (see that route for why we don't
 * use the Datadog Browser Logs SDK). Uses `navigator.sendBeacon` so emission
 * is non-blocking and survives page unload; falls back to `fetch` with
 * `keepalive: true` for environments without sendBeacon.
 *
 * The dispatcher in `wrapper.js` lives in an inline script and can't import
 * this module — it emits the same shape inline. Keep both in sync.
 */

export type ConsentEventName = 'consentResolved' | 'consentRevoked' | 'handlerError' | 'cookieSweep';

export interface ConsentEvent {
  event: ConsentEventName;
  activeGroups?: string[];
  previousActiveGroups?: string[];
  revokedCategories?: string[];
  /** Where in the dispatcher lifecycle a handler error occurred. */
  phase?: 'dispatch' | 'replay';
  /** Error message (extracted from Error to keep Datadog fields flat). */
  message?: string;
  /** Error stack (extracted from Error). */
  stack?: string;
  /** Number of cookies deleted during a cookieSweep. */
  swept?: number;
  /** Names of cookies preserved (matched the allowlist) during a cookieSweep. */
  kept?: string[];
}

const LOG_SOURCE = 'onetrust-consent';
const ENDPOINT = '/api/onetrust-event';

export function logConsentEvent(payload: ConsentEvent): void {
  // Detects the browser at runtime; there is no value to parse at a boundary here.
  // oxlint-disable-next-line anti-slop/no-runtime-typeof
  if (typeof window === 'undefined') return;
  try {
    // Inside the try so a non-serializable payload (circular ref, BigInt) can't
    // escape this function — observability must never throw into the dispatcher.
    const body = JSON.stringify({ source: LOG_SOURCE, ...payload });
    // Detects the browser at runtime; there is no value to parse at a boundary here.
    // oxlint-disable-next-line anti-slop/no-runtime-typeof
    if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
      if (navigator.sendBeacon(ENDPOINT, body)) return;
    }
    // Checks whether an optional callback was supplied before calling it.
    // oxlint-disable-next-line anti-slop/no-runtime-typeof
    if (typeof fetch === 'function') {
      fetch(ENDPOINT, { method: 'POST', body, keepalive: true }).catch(() => {});
    }
  } catch {
    // Observability must never throw inside the dispatcher.
  }
}
